import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/api";
import SeoHead from "@/lib/SeoHead";
import InquiryForm from "@/components/InquiryForm";
import { ArrowRight, ChevronRight, CheckCircle2, AlertTriangle, Wrench, ShieldCheck } from "lucide-react";

export default function IndustryDetail() {
  const { slug } = useParams();
  const [ind, setInd] = useState(null);
  const [products, setProducts] = useState([]);
  const [cases, setCases] = useState([]);
  const [siblings, setSiblings] = useState([]);

  useEffect(() => {
    setInd(null);
    api.get(`/industries/${slug}`).then((r) => setInd(r.data)).catch(() => setInd({ notFound: true }));
    api.get(`/industries`).then((r) => setSiblings((r.data || []).filter((i) => i.slug !== slug)));
    api.get(`/case-studies`).then((r) => setCases((r.data || []).filter((c) => (c.industry || "").toLowerCase().includes(slug.replace("-", " ")) || (c.industry || "").toLowerCase().includes(slug))));
  }, [slug]);

  useEffect(() => {
    if (ind?.key_products?.length) {
      api.get(`/products?limit=200`).then((r) => {
        const all = r.data || [];
        const matched = ind.key_products.map((s) => all.find((p) => p.slug === s)).filter(Boolean);
        setProducts(matched);
      });
    }
  }, [ind?.key_products?.join(",")]);

  if (!ind) return <div className="min-h-[60vh] flex items-center justify-center"><div className="w-10 h-10 border-2 border-[#C1121F] border-t-transparent rounded-full animate-spin" /></div>;
  if (ind.notFound) return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
      <h1 className="h-section text-[#1C1C1C]">Industry not found</h1>
      <Link to="/industries" className="btn-primary mt-6">All Industries</Link>
    </div>
  );

  const breadcrumbJsonLd = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
      { "@type": "ListItem", position: 2, name: "Industries", item: typeof window !== "undefined" ? window.location.origin + "/industries" : "" },
      { "@type": "ListItem", position: 3, name: ind.name },
    ],
  };

  return (
    <>
      <SeoHead
        title={ind.seo_title || `${ind.name} Fasteners | Durga Auto Industries`}
        description={ind.seo_description || ind.long_description?.slice(0, 155)}
        keywords={ind.seo_keywords}
        image={ind.cover_image || ind.image}
        jsonLd={breadcrumbJsonLd}
      />

      {/* Breadcrumb */}
      <div className="bg-[#F5F5F5] border-b border-[#E5E7EB]">
        <div className="container-x py-4 flex items-center gap-2 text-sm font-body text-[#4B5563]">
          <Link to="/" className="hover:text-[#C1121F]">Home</Link>
          <ChevronRight size={14} />
          <Link to="/industries" className="hover:text-[#C1121F]">Industries</Link>
          <ChevronRight size={14} />
          <span className="text-[#1C1C1C]">{ind.name}</span>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#1C1C1C] text-white pt-20 pb-20 border-b border-[#333] overflow-hidden" data-testid="industry-hero">
        {(ind.cover_image || ind.image) && (
          <div className="absolute inset-0 opacity-30">
            <img src={ind.cover_image || ind.image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/85 to-transparent" />
          </div>
        )}
        <div className="relative container-x">
          <div className="overline mb-5 flex items-center gap-3">
            <span className="w-10 h-px bg-[#C1121F]" /> Industries · {ind.name}
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-6xl max-w-3xl">
            Fasteners for the {ind.name} sector
          </h1>
          <p className="mt-6 body-lg text-white/75 font-body max-w-2xl">{ind.description}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary group" data-testid="industry-cta-quote">
              Get an industry-specific quote <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/products" className="btn-outline-light">View product catalog</Link>
          </div>
        </div>
      </section>

      {/* Overview content */}
      <section className="section-y bg-white" data-testid="industry-overview">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12">
          <div className="lg:sticky lg:top-28 lg:self-start space-y-6">
            <div>
              <div className="overline mb-3">Overview</div>
              <h2 className="h-section text-[#1C1C1C]">Why {ind.name.toLowerCase()} engineers choose Durga</h2>
            </div>
            <ul className="space-y-3 font-body text-sm text-[#1C1C1C]">
              {(ind.challenges || []).map((c, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ShieldCheck size={16} className="text-[#C1121F] flex-shrink-0 mt-0.5" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="prose max-w-none font-body text-[#1C1C1C] leading-relaxed">
            <p className="text-lg">{ind.long_description}</p>

            {ind.applications?.length > 0 && (
              <>
                <h3 className="font-heading font-semibold text-2xl text-[#1C1C1C] mt-10 mb-5">Typical applications in {ind.name}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
                  {ind.applications.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 border border-[#E5E7EB] hover:border-[#C1121F] transition-colors" data-testid={`industry-app-${i}`}>
                      <span className="w-6 h-6 bg-[#1C1C1C] text-[#C1121F] text-xs font-bold flex items-center justify-center flex-shrink-0 font-heading">{i + 1}</span>
                      <span className="font-body text-[#1C1C1C]">{a}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {ind.challenges?.length > 0 && (
              <>
                <h3 className="font-heading font-semibold text-2xl text-[#1C1C1C] mt-10 mb-5">Engineering challenges we solve</h3>
                <ul className="space-y-3 not-prose">
                  {ind.challenges.map((c, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <AlertTriangle size={18} className="text-[#C1121F] flex-shrink-0 mt-1" />
                      <span className="font-body text-[#1C1C1C]">{c}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {ind.content && (
              <div className="mt-10 whitespace-pre-line">{ind.content}</div>
            )}
          </div>
        </div>
      </section>

      {/* Recommended products */}
      {products.length > 0 && (
        <section className="section-y bg-[#F5F5F5]" data-testid="industry-products">
          <div className="container-x">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
              <div>
                <div className="overline mb-3">Recommended Products</div>
                <h2 className="h-section text-[#1C1C1C]">Fasteners commonly used in {ind.name}</h2>
              </div>
              <Link to="/products" className="font-heading font-semibold text-[#C1121F] inline-flex items-center gap-2">View all products <ArrowRight size={16} /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((p) => (
                <Link key={p.id} to={`/products/${p.slug}`} className="card-light overflow-hidden group" data-testid={`industry-product-${p.slug}`}>
                  <div className="aspect-[4/3] bg-white overflow-hidden">
                    {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
                  </div>
                  <div className="p-6">
                    <div className="overline mb-2">Product</div>
                    <h3 className="font-heading font-semibold text-lg text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{p.name}</h3>
                    <p className="mt-2 font-body text-sm text-[#4B5563] line-clamp-2">{p.short_description}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Case studies for this industry */}
      {cases.length > 0 && (
        <section className="section-y bg-white" data-testid="industry-cases">
          <div className="container-x">
            <div className="overline mb-3">Case Studies</div>
            <h2 className="h-section text-[#1C1C1C] mb-12">{ind.name} projects we've delivered</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cases.slice(0, 4).map((c) => (
                <Link key={c.id} to={`/case-studies/${c.slug}`} className="card-light p-8 group" data-testid={`industry-case-${c.slug}`}>
                  <div className="overline mb-3">{c.industry}</div>
                  <h3 className="font-heading font-semibold text-xl text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{c.title}</h3>
                  <p className="mt-3 font-body text-sm text-[#4B5563] line-clamp-3">{c.summary}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Inquiry CTA */}
      <section className="section-y bg-[#1C1C1C] text-white" data-testid="industry-inquiry">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 items-start">
          <div>
            <div className="overline mb-4">Industry Quote</div>
            <h2 className="h-section text-white">Working on a {ind.name.toLowerCase()} project?</h2>
            <p className="mt-6 body-lg text-white/70 font-body">
              Share your specifications, drawings or BOQ — our engineering team will respond within one
              business day with a complete quotation, lead time and material certifications.
            </p>
            <ul className="mt-8 space-y-3">
              {(ind.applications || []).slice(0, 4).map((a, i) => (
                <li key={i} className="flex items-start gap-3 font-body text-sm text-white/70">
                  <CheckCircle2 size={16} className="text-[#C1121F] flex-shrink-0 mt-0.5" /> {a}
                </li>
              ))}
            </ul>
          </div>
          <InquiryForm
            variant="dark"
            type="quote"
            productName={`${ind.name} fasteners`}
            title={`Quote for ${ind.name} project`}
            subtitle="We'll respond within 1 business day."
          />
        </div>
      </section>

      {/* Other industries */}
      {siblings.length > 0 && (
        <section className="section-y-sm bg-[#F5F5F5]" data-testid="industry-siblings">
          <div className="container-x">
            <h2 className="h-card text-[#1C1C1C] mb-8">Explore other industries</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {siblings.slice(0, 5).map((s) => (
                <Link key={s.id} to={`/industries/${s.slug}`} className="card-light p-5 text-center group" data-testid={`sibling-${s.slug}`}>
                  <Wrench size={20} className="text-[#C1121F] mx-auto" />
                  <div className="mt-3 font-heading font-semibold text-sm text-[#1C1C1C] group-hover:text-[#C1121F]">{s.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
